var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
/**
 * Centralized queue for sending NQSRequest items with retry handling.
 * Features:
 *  - Retry requests are processed before fresh ones
 *  - Failed retry stops further retries in that cycle; remaining retry requests are deferred
 *  - Only retry specific services
 *  - Retry queue is capped and sorted by timemark
 *  - Automatic retry scheduling with exponential backoff (decoupled from pings)
 *  - Max 25 retry attempts before flushing queue
 *
 * Enhancement:
 *  - If a new request is enqueued while processing, schedule one extra processing pass
 *    after the current cycle finishes so that newly enqueued fresh items are not stuck.
 */
export default class RequestQueue {
    constructor(params) {
        this.freshRequests = [];
        this.retryRequests = [];
        this.activeRequests = new Set();
        this.isProcessing = false;
        this.destroyed = false;
        // When enqueue occurs during processing, mark pending to run one more pass
        this.pendingAfterProcessing = false;
        // Automatic retry scheduling (decoupled from pings)
        this.retryInterval = null;
        this.retryAttemptCount = 0;
        this.baseRetryDelay = 5000;
        this.maxRetryAttempts = 25;
        this.fixedRetryAttempts = 10;
        this.retryServices = params.retryServices;
        this.maxRetryRequests = params.maxRetryRequests;
        this.onSuccess = params.onSuccess;
        this.onFail = params.onFail;
    }
    enqueue(request) {
        if (this.destroyed)
            return;
        this.freshRequests.push(request);
        if (this.isProcessing) {
            this.pendingAfterProcessing = true;
            return;
        }
        void this.process();
    }
    destroy() {
        this.destroyed = true;
        // Clear retry scheduling
        this._clearRetryInterval();
        // Abort all active requests
        const active = Array.from(this.activeRequests);
        this.activeRequests.clear();
        for (const req of active) {
            try {
                req.abort();
            }
            catch (_a) {
                // ignore
            }
        }
        // Clear queues
        this.freshRequests = [];
        this.retryRequests = [];
    }
    process() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.destroyed)
                return;
            if (this.isProcessing)
                return;
            this.isProcessing = true;
            try {
                // Take snapshots for this processing cycle
                const retryWorkingQueue = this.retryRequests.splice(0, this.retryRequests.length);
                const freshWorkingQueue = this.freshRequests.splice(0, this.freshRequests.length);
                const requestsToRetryLater = [];
                let retryRequestFailed = false;
                // 1) Process retry queue first – stop on first failure, defer the rest
                for (const request of retryWorkingQueue) {
                    if (this.destroyed)
                        return;
                    if (retryRequestFailed) {
                        requestsToRetryLater.push(request);
                        continue;
                    }
                    try {
                        this.activeRequests.add(request);
                        yield request.asyncSend();
                        this.activeRequests.delete(request);
                        // Invoke application callback on final success
                        const appSuccessCallback = request._appSuccessCallback;
                        if (appSuccessCallback) {
                            appSuccessCallback();
                        }
                        this.onSuccess(request);
                    }
                    catch (_a) {
                        this.activeRequests.delete(request);
                        retryRequestFailed = true;
                        requestsToRetryLater.push(request);
                        // Don't call app fail callback here - will retry
                        this.onFail(request);
                    }
                }
                // 2) Process fresh queue – retry only specific services
                for (const request of freshWorkingQueue) {
                    if (this.destroyed)
                        return;
                    try {
                        this.activeRequests.add(request);
                        yield request.asyncSend();
                        this.activeRequests.delete(request);
                        // Invoke application callback on success
                        const appSuccessCallback = request._appSuccessCallback;
                        if (appSuccessCallback) {
                            appSuccessCallback();
                        }
                        this.onSuccess(request);
                    }
                    catch (_b) {
                        this.activeRequests.delete(request);
                        this.onFail(request);
                        if (this.isRetryable(request)) {
                            // Will retry - don't call app fail callback yet
                            requestsToRetryLater.push(request);
                        }
                        else {
                            // Not retryable - invoke app fail callback now (final failure)
                            const appFailCallback = request._appFailCallback;
                            if (appFailCallback) {
                                appFailCallback();
                            }
                        }
                    }
                }
                // 3) Cap and order retry queue for next round
                if (requestsToRetryLater.length) {
                    // Get at most the last X requests to avoid growth
                    const capped = requestsToRetryLater.slice(-this.maxRetryRequests);
                    // Sort by timemark (ascending)
                    capped.sort((a, b) => {
                        var _a, _b, _c, _d;
                        const at = (_b = (_a = a.getParams()) === null || _a === void 0 ? void 0 : _a.timemark) !== null && _b !== void 0 ? _b : 0;
                        const bt = (_d = (_c = b.getParams()) === null || _c === void 0 ? void 0 : _c.timemark) !== null && _d !== void 0 ? _d : 0;
                        return at - bt;
                    });
                    this.retryRequests.push(...capped);
                }
            }
            finally {
                this.isProcessing = false;
                // If new fresh requests arrived during processing, run once more to flush them
                if (!this.destroyed && this.pendingAfterProcessing) {
                    this.pendingAfterProcessing = false;
                    // Only run another pass if there is something to process
                    if (this.freshRequests.length > 0) {
                        void this.process();
                    }
                }
            }
            // Schedule automatic retry if retry queue has requests (decoupled from pings!)
            if (!this.destroyed && this.retryRequests.length > 0) {
                this._scheduleRetryProcessing();
            }
            else {
                this._clearRetryInterval();
            }
        });
    }
    isRetryable(request) {
        const service = request.getService();
        return this.retryServices.includes(service);
    }
    /**
     * Schedules automatic retry processing for failed requests.
     * Uses exponential backoff after 10 attempts and stops after 25 attempts.
     * Decoupled from pings - works even after /stop event.
     * @private
     */
    _scheduleRetryProcessing() {
        // Guard against multiple timeouts or empty queue
        if (this.retryInterval || this.retryRequests.length === 0 || this.destroyed) {
            return;
        }
        // Check if max attempts reached
        if (this.retryAttemptCount >= this.maxRetryAttempts) {
            this._flushRetryQueue();
            return;
        }
        const delay = this._calculateRetryDelay();
        this.retryInterval = setTimeout(() => {
            this.retryInterval = null;
            // Extra safety check in case service was destroyed after timeout was created
            if (this.destroyed) {
                this._clearRetryInterval();
                return;
            }
            // Increment attempt counter
            this.retryAttemptCount++;
            // Check if max attempts reached
            if (this.retryAttemptCount >= this.maxRetryAttempts) {
                this._flushRetryQueue();
                return;
            }
            // Process requests - this will re-schedule if retries remain
            void this.process();
        }, delay);
    }
    /**
     * Calculates the delay for the next retry attempt.
     * First 10 attempts: fixed 5s delay
     * Attempts 11-25: exponential backoff with 2min cap
     * @private
     */
    _calculateRetryDelay() {
        if (this.retryAttemptCount < this.fixedRetryAttempts) {
            // First 10 attempts: fixed 5 second delay
            return this.baseRetryDelay;
        }
        // Attempts 11-25: exponential backoff
        const backoffAttempt = this.retryAttemptCount - this.fixedRetryAttempts;
        const exponentialDelay = this.baseRetryDelay * Math.pow(2, backoffAttempt);
        // Cap at 2 minutes (120000ms)
        return Math.min(exponentialDelay, 120000);
    }
    /**
     * Clears the active retry interval and resets attempt counter.
     * Called when queue is empty or service is destroyed.
     * @private
     */
    _clearRetryInterval() {
        if (this.retryInterval) {
            clearTimeout(this.retryInterval);
            this.retryInterval = null;
        }
        this.retryAttemptCount = 0;
    }
    /**
     * Flushes the retry queue when max attempts reached.
     * Calls fail callbacks for all pending requests and clears the queue.
     * @private
     */
    _flushRetryQueue() {
        if (this.retryRequests.length === 0) {
            return;
        }
        // Call fail callbacks for all pending requests (FINAL failure after all retries exhausted)
        this.retryRequests.forEach((request) => {
            // Invoke application callback on final failure
            const appFailCallback = request._appFailCallback;
            if (appFailCallback) {
                appFailCallback();
            }
            this.onFail(request);
        });
        // Clear the queue and reset state
        this.retryRequests = [];
        // Clear timeout if still active
        if (this.retryInterval) {
            clearTimeout(this.retryInterval);
            this.retryInterval = null;
        }
        // Reset attempt counter
        this.retryAttemptCount = 0;
    }
}
